<script setup lang="ts">
import { ref, computed, useTemplateRef } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { Calendar } from '@progress/kendo-vue-dateinputs';
import { DatePicker } from '@progress/kendo-vue-dateinputs';
import { BaseInputField } from '@/interfaces/InputFields';
import { onKeyStroke } from '@vueuse/core';

interface Props {
  label?: string;
  format?: string;
  minDate?: Date;
  maxDate?: Date;
  size?: 'small' | 'medium' | 'large';
  placeholder?: string;
  pickerType?: 'DMY' | 'MY';
}

const props = withDefaults(defineProps<BaseInputField & Props>(), {
  label: '',
  format: 'dd/MM/yyyy',
  size: 'small',
  placeholder: '',
  pickerType: 'DMY',
});

const model = defineModel<Date | undefined>();
const stopTodayKeybind = ref<(() => void) | undefined>();
const datepickerFocused = ref(false);
const calendarFocused = ref(false);
const datepickerRef = useTemplateRef('datepickerRef');
const calendarRef = useTemplateRef('calendarRef');

const datepickerOpen = computed(() => {
  return datepickerFocused.value || calendarFocused.value;
});

const onDatepickerFocus = () => {
  stopTodayKeybind.value = onKeyStroke(['t', 'T'], (e) => {
    const buttons = document.getElementsByClassName('k-calendar-nav-today');
    if (buttons.length >= 1) {
      (buttons[0] as HTMLButtonElement).click();
    }
  });
};

const onDatepickerClick = () => {
  datepickerFocused.value = true;
};

const onDatepickerBlur = () => {
  stopTodayKeybind.value?.();
  stopTodayKeybind.value = undefined;
  calendarFocused.value = false;

  setTimeout(() => {
    datepickerFocused.value = false;
  }, 250);
};

const onDatepickerChange = () => {
  calendarFocused.value = false;
};

const onDatepickerOpen = () => {
  datepickerFocused.value = true;
};

const onCalendarFocus = () => {
  calendarFocused.value = true;
};

const onCalendarBlur = () => {
  calendarFocused.value = false;
};

const onCalendarClose = () => {
  calendarFocused.value = false;
};

onClickOutside(
  calendarRef,
  () => {
    calendarFocused.value = false;
    datepickerFocused.value = false;
  },
  { ignore: [datepickerRef] }
);

const pickerFormat = computed(() => {
  return props.pickerType === 'MY' ? 'MMM yyyy' : props.format;
});

const viewSettings = computed(() => {
  if (props.pickerType === 'MY') {
    return {
      picker: 'month',
      defaultActiveView: 'year',
      depth: 'year',
      bottomView: 'year',
    };
  } else {
    return {
      picker: 'date',
      defaultActiveView: 'month',
      depth: 'month',
      bottomView: 'month',
    };
  }
});
</script>

<template>
  <DatePicker
    ref="datepickerRef"
    :placeholder="props.placeholder"
    @focus="onDatepickerFocus"
    :show="datepickerOpen"
    @blur="onDatepickerBlur"
    @change="onDatepickerChange"
    @open="onDatepickerOpen"
    @click="onDatepickerClick"
    v-model="model"
    :format="pickerFormat"
    :size="props.size"
    :disabled="props.disabled"
    calendar="calendar"
  >
    <template #calendar="{ props }">
      <Calendar
        ref="calendarRef"
        @focus="onCalendarFocus"
        @blur="onCalendarBlur"
        @close="onCalendarClose"
        v-bind="{
          ...(minDate ? { min: minDate } : {}),
          ...(maxDate ? { max: maxDate } : {}),
        }"
        :bottom-view="viewSettings.bottomView"
        @change="(e) => props.onChange(e)"
        :value="props.value"
      />
    </template>
  </DatePicker>
</template>
